import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  CalendarMonth,
  CheckCircle,
  CreditCard,
  GppGood,
  Schedule,
  Speed,
  SouthWest,
  AccountBalance,
} from '@mui/icons-material';
import {
  createCard,
  fetchCardProducts,
  fetchCardQuote,
  fetchCardWallet,
  fetchUserCardHolders,
} from '../hooks/hooks';

const money = (v, dp = 2) =>
  Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });

// Artwork only — brand, currency and limits come from the products endpoint.
// Mastercard is the only scheme we issue.
const PRODUCT_ART = [
  { grad: 'linear-gradient(135deg,#3a2c0c 0%,#8a6d1f 45%,#e0c063 100%)' },
  { grad: 'linear-gradient(135deg,#08251a 0%,#0d5c40 45%,#19b37c 100%)' },
  { grad: 'linear-gradient(135deg,#1a1a1f 0%,#33333d 45%,#6b6b7a 100%)' },
];

const USE_CASES = [
  'AI Subscriptions', 'Entertainment', 'Ads Platforms', 'Hosting & Cloud',
  'Travel & Accommodation', 'E-commerce', 'Gaming', 'SaaS & Tools',
];

const readError = (err, fallback) =>
  err?.response?.data?.message
  || (err?.response?.data?.errors && Object.values(err.response.data.errors).flat()[0])
  || fallback;

/** The physical-looking card preview. */
const CardArt = ({ gradient, brand, name, isNew }) => (
  <Box sx={{
    borderRadius: '16px', p: 2.5, color: '#fff', background: gradient,
    position: 'relative', overflow: 'hidden', minHeight: 190,
    boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
  }}>
    <Box sx={{ position: 'absolute', width: 240, height: 240, right: -110, top: -120,
               borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
      <Box sx={{ width: 38, height: 28, borderRadius: '5px',
                 background: 'linear-gradient(135deg,#d9b45a,#f2e0a4)', opacity: 0.95 }} />
      {isNew && (
        <Chip label="NEW" size="small"
              sx={{ height: 20, fontSize: 10, fontWeight: 800, bgcolor: 'rgba(255,255,255,0.9)',
                    color: '#111' }} />
      )}
    </Stack>

    <Typography sx={{ mt: 3.5, fontSize: 17, letterSpacing: 4, fontFamily: 'monospace',
                      color: 'rgba(255,255,255,0.85)' }}>
      ●●●●  ●●●●  ●●●●  ●●●●
    </Typography>

    <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mt={2.5}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 9, letterSpacing: 1.2, color: 'rgba(255,255,255,0.55)' }}>
          CARD HOLDER
        </Typography>
        <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase' }}>
          {name || '—'}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 1,
                        color: 'rgba(255,255,255,0.85)' }}>
        {brand || ''}
      </Typography>
    </Stack>
  </Box>
);

const FeatureTile = ({ icon: Icon, label, value, borderClr, cardBg }) => (
  <Paper elevation={0} sx={{ p: 1.75, borderRadius: '12px', border: `1px solid ${borderClr}`,
                             bgcolor: cardBg, height: '100%' }}>
    <Stack direction="row" gap={1.25} alignItems="center">
      <Box sx={{ width: 34, height: 34, borderRadius: '10px', flexShrink: 0,
                 display: 'flex', alignItems: 'center', justifyContent: 'center',
                 bgcolor: 'rgba(11,164,128,0.12)' }}>
        <Icon sx={{ fontSize: 18, color: '#0BA480' }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }} noWrap>{label}</Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>{value}</Typography>
      </Box>
    </Stack>
  </Paper>
);

const CardCreate = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const borderClr = isDark ? 'rgba(255,255,255,0.09)' : '#E2E8F0';

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [productIdx, setProductIdx] = useState(0);
  const [cardholderId, setCardholderId] = useState('');
  const [nameOnCard, setNameOnCard] = useState('');
  const [amount, setAmount] = useState('50');
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);

  const { data: wallet } = useQuery({ queryKey: ['cardWallet'], queryFn: fetchCardWallet });
  const { data: holders } = useQuery({ queryKey: ['myCardholders'], queryFn: fetchUserCardHolders });
  const { data: productData, isLoading: productsLoading } = useQuery({
    queryKey: ['cardProducts'], queryFn: fetchCardProducts, retry: false,
  });
  const { data: quote, isFetching: quoting } = useQuery({
    queryKey: ['cardQuote', amount],
    queryFn: () => fetchCardQuote({ amount }),
    enabled: Number(amount) > 0,
    retry: false,
  });

  const products = productData?.products || [];
  const product = products[productIdx];
  const art = PRODUCT_ART[productIdx % PRODUCT_ART.length];

  const holderList = useMemo(() => {
    const raw = holders?.data || holders?.results || (Array.isArray(holders) ? holders : []);
    return raw.filter((h) => h.card_holder_id);
  }, [holders]);

  // Default the printed name to the chosen cardholder.
  useEffect(() => {
    const h = holderList.find((x) => x.card_holder_id === cardholderId);
    if (h) setNameOnCard(`${h.first_name || ''} ${h.last_name || ''}`.trim().toUpperCase());
  }, [cardholderId, holderList]);

  const balance = Number(wallet?.card_balance_usd || 0);
  const total = Number(quote?.total_usd || 0);
  const short = total > 0 && total > balance;
  const minLoad = Number(quote?.minimum_first_load_usd || 50);

  const mutation = useMutation({
    mutationFn: createCard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      queryClient.invalidateQueries({ queryKey: ['cardWallet'] });
      navigate('/cards');
    },
    onError: (err) => setError(readError(err, 'Could not create the card.')),
  });

  const canSubmit = cardholderId && Number(amount) >= minLoad && !short
    && /^\d{4}$/.test(pin) && !mutation.isPending;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/cards')}
              sx={{ textTransform: 'none', mb: 2, color: 'text.secondary' }}>
        Back to cards
      </Button>

      {/* Header */}
      <Box sx={{
        borderRadius: '18px', p: 3, mb: 2.5, color: '#fff', position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(140deg, #0c1a14 0%, #092a1e 50%, #0d5c40 100%)',
      }}>
        <Box sx={{ position: 'absolute', width: 260, height: 260, right: -110, top: -130,
                   borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
               alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2} position="relative">
          <Box>
            <Stack direction="row" alignItems="center" gap={1}>
              <CreditCard sx={{ fontSize: 24 }} />
              <Typography fontSize={22} fontWeight={800}>Choose Your Virtual Card</Typography>
            </Stack>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 13.5, mt: 0.5, maxWidth: 560 }}>
              USD cards for online spending, issued instantly and accepted worldwide.
              Funded from your collection balance.
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.75} mt={1}>
              <GppGood sx={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }} />
              <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.6)' }}>
                3D Secure · Mastercard · Accepted worldwide
              </Typography>
            </Stack>
          </Box>
          <Paper elevation={0} sx={{ px: 2, py: 1.25, borderRadius: '10px',
                                     bgcolor: 'rgba(255,255,255,0.14)', color: '#fff' }}>
            <Typography fontSize={11} sx={{ color: 'rgba(255,255,255,0.7)' }}>Your card balance</Typography>
            <Typography fontSize={18} fontWeight={800}>${money(balance)}</Typography>
          </Paper>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}
                       onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2}>
        {/* Left: product and what it gives you */}
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: `1px solid ${borderClr}`,
                                     bgcolor: cardBg, mb: 2 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1,
                              color: 'text.secondary', mb: 1.5 }}>
              CHOOSE YOUR PRODUCT
            </Typography>

            {productsLoading ? (
              <Stack alignItems="center" py={3}><CircularProgress size={22} /></Stack>
            ) : products.length === 0 ? (
              <Alert severity="warning" sx={{ fontSize: 12.5 }}>
                No card products are available on your account right now.
              </Alert>
            ) : (
              <Grid container spacing={1.5}>
                {products.map((p, i) => {
                  const a = PRODUCT_ART[i % PRODUCT_ART.length];
                  const selected = i === productIdx;
                  return (
                    <Grid item xs={4} key={p.productId || i}>
                      <Box onClick={() => setProductIdx(i)} sx={{ cursor: 'pointer' }}>
                        <Box sx={{
                          borderRadius: '12px', height: 92, background: a.grad, position: 'relative',
                          outline: selected ? '2px solid #0BA480' : '2px solid transparent',
                          outlineOffset: 2, transition: 'outline-color .15s',
                        }}>
                          {selected && (
                            <CheckCircle sx={{ position: 'absolute', top: 6, right: 6,
                                               fontSize: 20, color: '#fff' }} />
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 12, fontWeight: selected ? 800 : 500,
                                          mt: 0.75, textAlign: 'center' }} noWrap>
                          {p.brand} {p.currency}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Paper>

          {product && (
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: `1px solid ${borderClr}`,
                                       bgcolor: cardBg, mb: 2 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1,
                                color: 'text.secondary', mb: 1.5 }}>
                WHAT YOU GET
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <FeatureTile icon={Speed} label="Spending limit" borderClr={borderClr} cardBg={cardBg}
                    value={product.spendingLimit
                      ? `$${money(product.spendingLimit, 0)} / ${(product.spendingPeriod || 'DAILY').toLowerCase()}`
                      : '—'} />
                </Grid>
                <Grid item xs={6}>
                  <FeatureTile icon={SouthWest} label="Funding fee" borderClr={borderClr} cardBg={cardBg}
                    value={quote ? `$${money(quote.funding_fee_usd)} on $${money(amount, 0)}` : '—'} />
                </Grid>
                <Grid item xs={6}>
                  <FeatureTile icon={CalendarMonth} label="Monthly fee" borderClr={borderClr} cardBg={cardBg}
                    value="$2" />
                </Grid>
                <Grid item xs={6}>
                  <FeatureTile icon={Schedule} label="Currency" borderClr={borderClr} cardBg={cardBg}
                    value={product.currency || 'USD'} />
                </Grid>
              </Grid>
              <Chip icon={<GppGood sx={{ fontSize: 15 }} />} label="3D Secure" size="small"
                    sx={{ mt: 1.5, fontSize: 11.5, fontWeight: 700,
                          bgcolor: 'rgba(11,164,128,0.12)', color: '#0BA480' }} />
            </Paper>
          )}

          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: `1px solid ${borderClr}`,
                                     bgcolor: cardBg }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1,
                              color: 'text.secondary', mb: 1.5 }}>
              WHERE TO USE
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {USE_CASES.map((u) => (
                <Chip key={u} label={u} size="small" variant="outlined"
                      sx={{ fontSize: 11.5, borderColor: borderClr }} />
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* Right: preview and the form */}
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', border: `1px solid ${borderClr}`,
                                     bgcolor: cardBg, position: { md: 'sticky' }, top: 16 }}>
            <CardArt gradient={art.grad} brand={product?.brand} name={nameOnCard} isNew />

            <Stack direction="row" alignItems="baseline" gap={1} justifyContent="center" my={2}>
              <Typography fontSize={30} fontWeight={800}>
                ${money(quote?.issuance_fee_usd || 0, 0)}
              </Typography>
              <Typography fontSize={13} color="text.secondary">card issuance fee</Typography>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1,
                              color: 'text.secondary', mb: 1 }}>
              CREATE YOUR CARD
            </Typography>

            <TextField
              size="small" margin="dense" fullWidth select label="Cardholder"
              value={cardholderId} onChange={(e) => setCardholderId(e.target.value)}
              helperText={holderList.length
                ? 'Verification must be approved by the issuer'
                : 'Add a cardholder first'}
            >
              {holderList.map((h) => (
                <MenuItem key={h.card_holder_id} value={h.card_holder_id}>
                  {h.first_name} {h.last_name}{h.kyc_status ? ` — ${h.kyc_status}` : ''}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small" margin="dense" fullWidth label="Name on Card"
              value={nameOnCard}
              onChange={(e) => setNameOnCard(e.target.value.toUpperCase().slice(0, 25))}
              inputProps={{ maxLength: 25 }}
            />

            <TextField
              size="small" margin="dense" fullWidth type="number" label="Initial Balance"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              error={Number(amount) > 0 && Number(amount) < minLoad}
              helperText={Number(amount) > 0 && Number(amount) < minLoad
                ? `Minimum $${money(minLoad, 0)}`
                : ' '}
            />

            <Paper elevation={0} sx={{ p: 1.75, borderRadius: '10px', mt: 1,
                                       bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#F8FAFC' }}>
              {quoting ? (
                <Typography fontSize={12.5} color="text.secondary">Calculating…</Typography>
              ) : quote ? (
                <>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography fontSize={12.5} color="text.secondary">
                      Issuance ${money(quote.issuance_fee_usd)} + funding ${money(quote.funding_fee_usd)}
                      {' '}+ balance ${money(quote.card_balance_usd)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" mt={0.75}>
                    <Typography fontSize={13} fontWeight={700}>Total</Typography>
                    <Typography fontSize={20} fontWeight={800} color={short ? '#D32F2F' : '#0BA480'}>
                      ${money(quote.total_usd)}
                    </Typography>
                  </Stack>
                  <Typography fontSize={11.5} color="text.secondary">
                    ≈ TZS {money(quote.total_tzs, 0)} at {money(quote.usd_tzs_rate, 2)}
                  </Typography>
                  {short && (
                    <Typography fontSize={12} color="error" mt={0.5}>
                      Add ${money(total - balance)} to your card balance first.
                    </Typography>
                  )}
                </>
              ) : (
                <Typography fontSize={12.5} color="text.secondary">
                  Enter an amount to see the cost.
                </Typography>
              )}
            </Paper>

            <TextField
              size="small" margin="dense" fullWidth type="password" label="PIN"
              value={pin} onChange={(e) => setPin(e.target.value)}
              inputProps={{ maxLength: 4, inputMode: 'numeric' }}
              sx={{ mt: 1.5 }}
            />

            <Button
              fullWidth variant="contained" endIcon={<ArrowForward />}
              disabled={!canSubmit}
              onClick={() => {
                setError(null);
                mutation.mutate({
                  cardholderId,
                  amount_usd: Number(amount),
                  ...(product?.productId ? { productId: product.productId } : {}),
                  ...(nameOnCard ? { name: nameOnCard } : {}),
                  pin,
                });
              }}
              sx={{ mt: 2, py: 1.3, borderRadius: '12px', textTransform: 'none',
                    fontSize: 15, fontWeight: 700, boxShadow: 0, backgroundColor: '#0BA480',
                    '&:hover': { boxShadow: 'none', backgroundColor: '#0A8F70' } }}
            >
              {mutation.isPending ? 'Creating…' : 'Create'}
            </Button>

            <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5} mt={1}>
              <AccountBalance sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography fontSize={11.5} color="text.secondary" textAlign="center">
                Confirmed with your PIN · charged to your card balance
              </Typography>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CardCreate;
